function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { cloneElement, useRef } from 'react';
import styled, { css } from 'styled-components';
import { shape, string, bool, func, oneOf, node } from 'prop-types';
import VisuallyHiddenText from '@bbc/psammead-visually-hidden-text';
import { navigationIcons } from '@bbc/psammead-assets/svgs';
import { C_WHITE, C_EBON, C_SHADOW } from '@bbc/psammead-styles/colours';
import { GEL_SPACING_HLF, GEL_SPACING, GEL_SPACING_DBL } from '@bbc/gel-foundations/spacings';
import { Helmet } from 'react-helmet';
import { GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_B_MIN_WIDTH } from '@bbc/gel-foundations/breakpoints';
import { getPica } from '@bbc/gel-foundations/typography';
import { scriptPropType } from '@bbc/gel-foundations/prop-types';
import { getSansRegular } from '@bbc/psammead-styles/font-styles';
export var NAV_BAR_TOP_BOTTOM_SPACING = 0.75; // 12px

var getStyles = function getStyles(dir) {
  var direction = dir === 'ltr' ? 'left' : 'right';
  return "border-".concat(direction, ": ").concat(GEL_SPACING_HLF, " solid ").concat(C_WHITE, ";\n          padding-").concat(direction, ": ").concat(GEL_SPACING, ";");
};

var StyledDropdown = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledDropdown",
  componentId: "ukxuly-0"
})(["background-color:", ";clear:both;overflow:hidden;height:0;transition:all 0.2s ease-out;transition-timing-function:cubic-bezier(0,0,0.58,1);", " @media (min-width:", "){display:none;visibility:hidden;}@media (prefers-reduced-motion:reduce){transition:none;}"], C_EBON, function (_ref) {
  var height = _ref.height,
      isOpen = _ref.isOpen;
  return isOpen ? "visibility: visible; height: ".concat(height, "px;") : "visibility: hidden;";
}, GEL_GROUP_3_SCREEN_WIDTH_MIN);
export var CanonicalDropdown = function CanonicalDropdown(_ref2) {
  var isOpen = _ref2.isOpen,
      children = _ref2.children;
  var heightRef = useRef(null);
  return /*#__PURE__*/React.createElement(StyledDropdown, {
    ref: heightRef,
    height: heightRef.current ? heightRef.current.scrollHeight : 0,
    isOpen: isOpen
  }, children);
};
CanonicalDropdown.propTypes = {
  isOpen: bool.isRequired,
  children: node.isRequired
};
export var AmpDropdown = /*#__PURE__*/styled.div.withConfig({
  displayName: "AmpDropdown",
  componentId: "ukxuly-1"
})(["background-color:", ";clear:both;@media (min-width:", "){display:none;visibility:hidden;}"], C_EBON, GEL_GROUP_3_SCREEN_WIDTH_MIN);
export var DropdownUl = /*#__PURE__*/styled.ul.attrs({
  role: 'list'
}).withConfig({
  displayName: "DropdownUl",
  componentId: "ukxuly-2"
})(["list-style-type:none;margin:0;padding:0 ", ";border-bottom:0.125rem solid ", ";"], GEL_SPACING, C_SHADOW);
var StyledDropdownLi = /*#__PURE__*/styled.li.withConfig({
  displayName: "StyledDropdownLi",
  componentId: "ukxuly-3"
})(["padding:0.75rem 0;border-bottom:0.0625rem solid ", ";&:last-child{padding-bottom:", ";border:0;}"], C_SHADOW, GEL_SPACING_DBL);
var StyledDropdownLink = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledDropdownLink",
  componentId: "ukxuly-4"
})(["", ";", " color:", ";text-decoration:none;&:hover,&:focus{text-decoration:underline;}"], function (_ref3) {
  var script = _ref3.script;
  return script && getPica(script);
}, function (_ref4) {
  var service = _ref4.service;
  return service && getSansRegular(service);
}, C_WHITE);
var StyledCurrentLink = /*#__PURE__*/styled.span.attrs({
  role: 'text'
}).withConfig({
  displayName: "StyledCurrentLink",
  componentId: "ukxuly-5"
})(["", ""], function (_ref5) {
  var dir = _ref5.dir;
  return getStyles(dir);
});
export var DropdownLi = function DropdownLi(_ref6) {
  var children = _ref6.children,
      script = _ref6.script,
      currentPageText = _ref6.currentPageText,
      active = _ref6.active,
      service = _ref6.service,
      url = _ref6.url,
      dir = _ref6.dir;
  return /*#__PURE__*/React.createElement(StyledDropdownLi, {
    role: "listitem"
  }, /*#__PURE__*/React.createElement(StyledDropdownLink, {
    script: script,
    service: service,
    href: url
  }, active && currentPageText ? /*#__PURE__*/React.createElement(StyledCurrentLink, {
    dir: dir
  }, /*#__PURE__*/React.createElement(VisuallyHiddenText, null, currentPageText, ", "), children) : children));
};
DropdownLi.propTypes = {
  children: string.isRequired,
  url: string.isRequired,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired,
  active: bool,
  currentPageText: string,
  dir: oneOf(['ltr', 'rtl'])
};
DropdownLi.defaultProps = {
  active: false,
  currentPageText: null,
  dir: 'ltr'
};
var iconBorder = /*#__PURE__*/css(["content:'';position:absolute;left:0;right:0;bottom:0;top:0;border:", " solid ", ";"], GEL_SPACING_HLF, C_WHITE); // The sideLength of the button should be
//  line height + top padding + bottom padding

var calculateButtonSide = function calculateButtonSide(lineHeight) {
  return lineHeight / 16 + NAV_BAR_TOP_BOTTOM_SPACING * 2;
};

var getButtonDimensions = function getButtonDimensions(lineHeight) {
  return "height: ".concat(calculateButtonSide(lineHeight), "rem;\n  width: ").concat(calculateButtonSide(lineHeight), "rem;");
}; // React allows us to put non-standard HTML attributes onto elements
// whereas styled-components filters them out.
// The `on` amp attribute is classed as non-standard, therefore we
// make a react element which spreads the props as attributes
// and style the React button.
// For some reason the styled-components filtering only happens on SSR
// so is not visible in tests or storybook, only once everything is
// integrated into simorgh.
// eslint-disable-next-line react/prop-types


var Button = function Button(_ref7) {
  var script = _ref7.script,
      props = _objectWithoutProperties(_ref7, ["script"]);

  return /*#__PURE__*/React.createElement("button", _extends({
    type: "button"
  }, props));
};

var MenuButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "MenuButton",
  componentId: "ukxuly-6"
})(["position:relative;padding:0;margin:0;background-color:transparent;border:0;", " ", " &:hover,&:focus{cursor:pointer;box-shadow:inset 0 0 0 ", " ", ";::after{", ";}}@media (min-width:", "){display:none;visibility:hidden;}@media (min-width:", "rem){", "}& svg{vertical-align:middle;}"], function (_ref8) {
  var dir = _ref8.dir;
  return dir === 'ltr' ? "float: left;" : "float: right;";
}, function (_ref9) {
  var script = _ref9.script;
  return script && getButtonDimensions(script.pica.groupA.lineHeight);
}, GEL_SPACING_HLF, C_WHITE, iconBorder, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_B_MIN_WIDTH, function (_ref10) {
  var script = _ref10.script;
  return script && getButtonDimensions(script.pica.groupB.lineHeight);
});
export var CanonicalMenuButton = function CanonicalMenuButton(_ref11) {
  var announcedText = _ref11.announcedText,
      isOpen = _ref11.isOpen,
      onClick = _ref11.onClick,
      dir = _ref11.dir,
      script = _ref11.script;
  return /*#__PURE__*/React.createElement(MenuButton, {
    onClick: onClick,
    "aria-expanded": isOpen ? 'true' : 'false',
    dir: dir,
    script: script
  }, isOpen ? navigationIcons.cross : navigationIcons.hamburger, /*#__PURE__*/React.createElement(VisuallyHiddenText, null, announcedText));
};
CanonicalMenuButton.propTypes = {
  announcedText: string.isRequired,
  onClick: func.isRequired,
  isOpen: bool.isRequired,
  dir: oneOf(['ltr', 'rtl']),
  script: shape(scriptPropType).isRequired
};
CanonicalMenuButton.defaultProps = {
  dir: 'ltr'
};

var AmpHead = function AmpHead() {
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    async: true,
    "custom-element": "amp-bind",
    src: "https://cdn.ampproject.org/v0/amp-bind-0.1.js"
  }));
};

var expandedHandler = 'AMP.setState({ menuState: { expanded: !menuState.expanded }})';
var initialState = {
  expanded: false
};
export var AmpMenuButton = function AmpMenuButton(_ref12) {
  var announcedText = _ref12.announcedText,
      onToggle = _ref12.onToggle,
      dir = _ref12.dir,
      script = _ref12.script;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AmpHead, null), /*#__PURE__*/React.createElement("amp-state", {
    id: "menuState"
  }, /*#__PURE__*/React.createElement("script", {
    type: "application/json"
    /* eslint-disable-next-line react/no-danger */
    ,
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(initialState)
    }
  })), /*#__PURE__*/React.createElement(MenuButton, {
    "aria-expanded": "false",
    "data-amp-bind-aria-expanded": "menuState.expanded ? \"true\" : \"false\"",
    on: "tap:".concat(expandedHandler, ",").concat(onToggle),
    dir: dir,
    script: script
  }, /*#__PURE__*/cloneElement(navigationIcons.hamburger, {
    'data-amp-bind-hidden': 'menuState.expanded'
  }), /*#__PURE__*/cloneElement(navigationIcons.cross, {
    hidden: true,
    'data-amp-bind-hidden': '!menuState.expanded'
  }), /*#__PURE__*/React.createElement(VisuallyHiddenText, null, announcedText)));
};
AmpMenuButton.propTypes = {
  announcedText: string.isRequired,
  onToggle: string.isRequired,
  dir: oneOf(['ltr', 'rtl']),
  script: shape(scriptPropType).isRequired
};
AmpMenuButton.defaultProps = {
  dir: 'ltr'
};